import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import {
  FaPlaneDeparture,
  FaHome,
  FaShieldAlt,
  FaGlobe,
  FaMapMarkedAlt,
  FaFileAlt,
} from 'react-icons/fa';

import { Headercomponent } from '../components/headercomponent';
import NodeBackground from '../components/nodeBackground';
import DemoModal from '../components/demoModal';

Modal.setAppElement('#root');

const ServiceBadge = ({ children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '0.35rem 0.7rem',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: '999px',
      fontSize: '0.8rem',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      marginBottom: '0.9rem',
      opacity: 0.9,
    }}
  >
    {children}
  </span>
);

const serviceIconMap = {
  'Executive Travel': <FaPlaneDeparture />,
  'Residential Decisions': <FaHome />,
  'Executive Exposure': <FaShieldAlt />,
  'Threat Monitoring': <FaGlobe />,
  'Event Intelligence': <FaMapMarkedAlt />,
  'Custom Intelligence': <FaFileAlt />,
};

const ServiceGraphic = ({ title, items, onItemClick }) => (
  <div className="service-graphic">
    <div className="service-graphic-header">
      <h3>{title}</h3>
    </div>

    <div className="service-graphic-grid">
      {items.map((item) => (
        <button
          key={item}
          type="button"
          className="service-graphic-item"
          onClick={() => onItemClick?.(item)}
        >
          <div className="service-graphic-left">
            <span className="service-graphic-icon">
              {serviceIconMap[item] || <FaFileAlt />}
            </span>
            <span>{item}</span>
          </div>

          <span className="service-arrow">→</span>
        </button>
      ))}
    </div>
  </div>
);

function HowClientsUseWeathervane() {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [serviceModal, setServiceModal] = useState(null);

  const handleModal = () => setModalIsOpen((current) => !current);

  const clientNeeds = [
    {
      badge: 'Executive Travel',
      title: 'Before travel, clients need to know what the destination actually means for safety.',
      text:
        'Weathervane turns hotel locations, venue areas, arrival routes, surrounding neighborhoods, crime patterns, public safety indicators, and open-source context into practical travel intelligence. The deliverable helps executive staff, family offices, and protective teams understand where risk is concentrated, what areas deserve attention, and what planning considerations should be addressed before arrival.',
      graphicTitle: 'Travel Intelligence Questions',
      items: [
        'Executive Travel',
        'Event Intelligence',
        'Custom Intelligence',
      ],
    },
    {
      badge: 'Residential Decisions',
      title: 'Before buying, leasing, or relocating, clients need neighborhood-level context.',
      text:
        'General citywide crime statistics are rarely useful for high-value residential decisions. Weathervane uses mapped crime data, historical patterns, nearby incident concentrations, and custom location analysis to explain what reported crime around a property actually means for a family, advisor, real estate professional, or security team.',
      graphicTitle: 'Property Intelligence Questions',
      items: [
        'Residential Decisions',
        'Executive Exposure',
        'Custom Intelligence',
      ],
    },
    {
      badge: 'Executive Exposure',
      title: 'Before risk can be reduced, clients need to know what is publicly visible.',
      text:
        'Executives, founders, family members, and public-facing individuals often have personal information spread across public records, people-search sites, social platforms, corporate filings, media, and other open sources. Weathervane identifies exposure indicators, explains why they matter, and provides mitigation-oriented findings in a format staff can act on.',
      graphicTitle: 'Exposure Intelligence Questions',
      items: [
        'Executive Exposure',
        'Threat Monitoring',
        'Custom Intelligence',
      ],
    },
    {
      badge: 'Threat Monitoring',
      title: 'When online attention escalates, clients need more than keyword alerts.',
      text:
        'Weathervane combines analyst review with custom-coded threat detection workflows to surface relevant comments, narratives, and indicators of concern from public online sources. The platform helps analysts and protective teams focus on what may matter, instead of manually sorting through large volumes of irrelevant content.',
      graphicTitle: 'Monitoring Questions',
      items: [
        'Threat Monitoring',
        'Executive Exposure',
        'Custom Intelligence',
      ],
    },
    {
      badge: 'Events and Venues',
      title: 'Before an event, clients need to understand the location, audience, and surrounding environment.',
      text:
        'For meetings, appearances, fundraisers, conferences, and private events, Weathervane can assess the venue area, surrounding crime patterns, public safety conditions, online discussion, protest indicators, and operational considerations. The result is a decision-focused intelligence product designed to support planning before people arrive on site.',
      graphicTitle: 'Event Intelligence Questions',
      items: [
        'Event Intelligence',
        'Executive Travel',
        'Threat Monitoring',
      ],
    },
    {
      badge: 'Custom OSINT',
      title: 'When the question is unique, the intelligence product should be built around the decision.',
      text:
        'Some client concerns do not fit a standard template. Weathervane scopes the intelligence question, identifies relevant sources, performs analyst-led review, builds custom visuals or code where needed, and delivers findings, gaps, and recommendations in an executive-ready format.',
      graphicTitle: 'Custom Intelligence Questions',
      items: [
        'Custom Intelligence',
        'Threat Monitoring',
        'Residential Decisions',
      ],
    },
  ];

  const serviceDetails = {
    'Executive Travel': {
      title: 'Executive Travel Intelligence',
      need:
        'A hotel, venue, or destination can appear acceptable online while still creating avoidable risk. Travel intelligence helps staff and protective teams understand the area before decisions are finalized.',
      includes: [
        'Destination and hotel-area review',
        'Nearby crime and safety context',
        'Arrival, movement, parking, and venue-area considerations',
        'Relevant public safety, disruption, or protest indicators when applicable',
        'Plain-language recommendations for planning',
      ],
      for:
        'Executive assistants, chiefs of staff, family offices, private travelers, and executive protection teams.',
    },
    'Residential Decisions': {
      title: 'Residential and Property Intelligence',
      need:
        'Clients evaluating homes, rentals, schools, or investment properties need block-level context, not broad citywide averages.',
      includes: [
        'Property-centered crime mapping',
        'Historical crime trends near the address',
        'Violent, property, theft, and vehicle-related indicators',
        'Neighborhood comparison and surrounding-area context',
        'Analyst summary for non-technical decision makers',
      ],
      for:
        'Families, real estate advisors, relocation professionals, family offices, and security teams evaluating residential decisions.',
    },
    'Executive Exposure': {
      title: 'Executive Exposure Assessment',
      need:
        'Publicly available personal information can create protective, reputational, and operational concerns for executives and families.',
      includes: [
        'Public profile and people-search exposure review',
        'Address, contact, family, and household exposure indicators',
        'Social media and public-facing risk notes',
        'Findings written for staff and decision makers',
        'Mitigation-oriented recommendations',
      ],
      for:
        'Executives, founders, family members, public figures, family offices, and protective intelligence teams.',
    },
    'Threat Monitoring': {
      title: 'Protective Intelligence Monitoring',
      need:
        'Relevant warning indicators are often buried in comment sections, forums, and smaller online communities. Monitoring helps surface items that may deserve review.',
      includes: [
        'Custom-coded collection and triage workflows',
        'Natural-language review for threat and concern indicators',
        'Prioritized findings for analyst review',
        'Support for escalation-ready reporting',
        'Recurring intelligence summaries when needed',
      ],
      for:
        'Executive protection teams, corporate security teams, analysts, public-facing organizations, and high-profile individuals.',
    },
    'Event Intelligence': {
      title: 'Event Intelligence Support',
      need:
        'Events create temporary visibility, concentrated exposure, and time-sensitive planning concerns. Intelligence support helps teams understand the environment before and during the event.',
      includes: [
        'Venue and surrounding-area review',
        'Nearby crime and public safety context',
        'Online attention, protest, or disruption indicators',
        'Movement and arrival considerations',
        'Post-event intelligence summary when requested',
      ],
      for:
        'Event planners, executive protection details, corporate teams, private event hosts, and organizations supporting high-visibility gatherings.',
    },
    'Custom Intelligence': {
      title: 'Custom OSINT and Risk Intelligence',
      need:
        'Some decisions require a scoped intelligence product built around a specific person, location, event, organization, or concern.',
      includes: [
        'Defined intelligence question and scope',
        'Open-source collection and source review',
        'Crime, location, exposure, or online-risk analysis as needed',
        'Custom visuals, maps, timelines, or coded workflows where useful',
        'Findings, intelligence gaps, and recommended next steps',
      ],
      for:
        'Family offices, attorneys, investigators, corporate security teams, executive staff, and private clients with specific intelligence questions.',
    },
  };

  return (
    <>
      <Headercomponent />

      <div className="container">
        <div className="hero">
          <NodeBackground />

          <div className="hero-content">
            <h1>How Clients Use Weathervane</h1>
            <p>
              Intelligence support for executives, families, staff, advisors, and security teams who need
              clear answers before making decisions involving travel, property, exposure, events, or online risk.
            </p>

            <button className="cta-button" onClick={handleModal}>
              Discuss a Private Assessment
            </button>

            <div className="hero-proof-row">
              <span>Research-led intelligence</span>
              <span>Custom-coded workflows</span>
              <span>Executive-ready deliverables</span>
              <span>Decision-focused findings</span>
            </div>
          </div>

          <div className="hero-diagram">
            <ServiceGraphic
              title="Client Intelligence Questions"
              items={[
                'Executive Travel',
                'Residential Decisions',
                'Executive Exposure',
                'Threat Monitoring',
                'Event Intelligence',
                'Custom Intelligence',
              ]}
              onItemClick={(item) => {
                if (serviceDetails[item]) {
                  setServiceModal(serviceDetails[item]);
                }
              }}
            />
          </div>
        </div>

        <section className="platform-overview">
          <h2>Gain actionable intelligence to answer your questions.</h2>
          <p>
            A family office may need to understand whether a property creates avoidable exposure. A chief
            of staff may need to know whether a hotel area is appropriate for an executive visit. A
            protection team may need to know whether online attention is escalating into a physical concern. Weathervane uses structured intelligence techniques
            to collect and analyze data to anser those questions. Our custom 
            intelligence products are supported by research, analysis, mapping,
            data review, and custom software workflows where needed.
          </p>

          <div className="overview-cards">
            {clientNeeds.map((need, index) => (
              <div
                className={`overview-feature ${
                  index === 1 || index === 3 || index === 5 ? 'overview-feature-reverse' : ''
                }`}
                key={need.title}
              >
                <div className="feature-graphic">
                  <ServiceGraphic
                    title={need.graphicTitle}
                    items={need.items}
                    onItemClick={(item) => {
                      if (serviceDetails[item]) {
                        setServiceModal(serviceDetails[item]);
                      }
                    }}
                  />
                </div>

                <div className="feature-text">
                  <ServiceBadge>{need.badge}</ServiceBadge>
                  <h3>{need.title}</h3>
                  <p>{need.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="process-deliverables-section">
          <div className="process-column">
            <span className="section-kicker">How Clients Engage</span>
            <h2>From Concern to Intelligence Product</h2>
            <p>
              Every engagement starts with the decision the client needs to make. We scope the question,
              identify relevant sources, analyze what matters, and deliver a product that supports action.
            </p>

            <div className="process-steps">
              <div className="process-step">
                <span>01</span>
                <div>
                  <h3>Define the Decision</h3>
                  <p>
                    We clarify the person, property, destination, event, organization, or concern that
                    needs intelligence support.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>02</span>
                <div>
                  <h3>Research and Build</h3>
                  <p>
                    We collect relevant public information, structure data, map locations, review online
                    indicators, and build custom workflows or visuals when useful.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>03</span>
                <div>
                  <h3>Assess What Matters</h3>
                  <p>
                    Analyst judgment separates raw information from findings that may affect security,
                    travel, residential, reputational, or operational decisions.
                  </p>
                </div>
              </div>

              <div className="process-step">
                <span>04</span>
                <div>
                  <h3>Deliver the Product</h3>
                  <p>
                    Clients receive a clear intelligence product with findings, visuals, gaps, and
                    recommendations written for decision makers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="deliverables-column">
            <span className="section-kicker">What Clients Receive</span>
            <h2>Deliverables Built for Action</h2>
            <p>
              Weathervane products are designed to be useful to both staff and security professionals:
              clear enough for executive review, detailed enough to support protective planning.
            </p>

            <div className="deliverables-grid">
              <div className="deliverable-card">
                <h3>BLUF Summary</h3>
                <p>A concise bottom-line assessment of the most important findings.</p>
              </div>

              <div className="deliverable-card">
                <h3>Maps and Visuals</h3>
                <p>Crime maps, exposure visuals, timelines, dashboards, and location graphics.</p>
              </div>

              <div className="deliverable-card">
                <h3>Research Findings</h3>
                <p>Relevant open-source, crime, public-record, and online-risk findings.</p>
              </div>

              <div className="deliverable-card">
                <h3>Analyst Judgment</h3>
                <p>Plain-language explanation of what matters, why it matters, and what is unknown.</p>
              </div>

              <div className="deliverable-card">
                <h3>Custom Workflows</h3>
                <p>Custom-coded tools, data structuring, mapping, and triage logic where needed.</p>
              </div>

              <div className="deliverable-card">
                <h3>Recommendations</h3>
                <p>Practical considerations tied directly to the findings and client decision.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Bring the intelligence question. We will build the product around it.</h2>
          <p>
            Use Weathervane for one-time assessments, recurring intelligence products, or platform-supported
            monitoring for executives, families, organizations, properties, travel, events, and sensitive
            open-source concerns.
          </p>

          <button className="cta-button" onClick={handleModal}>
            Start a Private Conversation
          </button>
        </section>

        <DemoModal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)} />

        <Modal
          isOpen={!!serviceModal}
          onRequestClose={() => setServiceModal(null)}
          className="service-detail-modal"
          overlayClassName="report-preview-overlay"
        >
          {serviceModal && (
            <>
              <div className="report-preview-header">
                <h3>{serviceModal.title}</h3>

                <button
                  type="button"
                  className="report-preview-close"
                  onClick={() => setServiceModal(null)}
                >
                  ×
                </button>
              </div>

              <div className="service-detail-content">
                <section>
                  <h4>Why It Matters</h4>
                  <p>{serviceModal.need}</p>
                </section>

                <section>
                  <h4>What This Includes</h4>
                  <ul>
                    {serviceModal.includes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section>
                  <h4>Who It Is For</h4>
                  <p>{serviceModal.for}</p>
                </section>
              </div>
            </>
          )}
        </Modal>
      </div>
    </>
  );
}

export default HowClientsUseWeathervane;